import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarRange, Filter, Search, SlidersHorizontal } from 'lucide-react';

import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  FilterBuilder,
  Pagination,
  StatusPill,
  type FilterCondition,
  type FilterField,
  type StatusTone,
  type ColumnDef,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useReconciliationCycles,
  formatVND,
  formatDateVN,
  type CycleListItem,
  type CycleStatus,
  type ReconciliationCyclesParams,
} from '@/api/reconciliation';
import { TableLink } from '@/components/table-link';
import { paginationView } from '@/pages/customers/pagination-summary';
import { TabLoading } from '@/pages/customers/detail/tab-states';

const PAGE_SIZE = 20;

/* ── Status pill ─────────────────────────────────────────────────────────── */

const STATUS_LABEL: Record<CycleStatus, string> = {
  reconciling: 'Reconciling',
  done: 'Done',
};

const STATUS_TONE: Record<CycleStatus, StatusTone> = {
  reconciling: 'warning',
  done: 'success',
};

/* ── Filters ─────────────────────────────────────────────────────────────── */

const FILTER_FIELDS: FilterField[] = [
  { value: 'period', label: 'Period', input: 'date' },
  {
    value: 'status',
    label: 'Status',
    input: 'select',
    options: [
      { value: 'reconciling', label: 'Reconciling' },
      { value: 'done', label: 'Done' },
    ],
  },
];

function CycleStatusPill({ status }: { status: CycleStatus }) {
  const tone = STATUS_TONE[status] ?? 'neutral';
  const label = STATUS_LABEL[status] ?? status;
  return <StatusPill tone={tone}>{label}</StatusPill>;
}

/* ── Columns ─────────────────────────────────────────────────────────────── */

const columns: ColumnDef<CycleListItem, unknown>[] = [
  {
    id: 'period',
    header: 'Reconciliation Period',
    cell: ({ row }) => (
      <TableLink to={`/reconciliation/${encodeURIComponent(row.original.cohort_key)}`}>
        {formatDateVN(row.original.period_startdate)} – {formatDateVN(row.original.period_enddate)}
      </TableLink>
    ),
  },
  {
    id: 'total_services',
    header: 'Total Services',
    cell: ({ row }) => (
      <span style={{ color: 'var(--vn-fg-default)' }}>{row.original.total_services}</span>
    ),
  },
  {
    id: 'issued_count',
    header: 'Issued',
    cell: ({ row }) => (
      <span style={{ color: 'var(--vn-fg-default)' }}>{row.original.issued_count}</span>
    ),
  },
  {
    id: 'pending_count',
    header: 'Pending',
    cell: ({ row }) => (
      <span
        style={{
          color:
            row.original.pending_count > 0
              ? 'var(--vn-warning-text)'
              : 'var(--vn-fg-muted)',
        }}
      >
        {row.original.pending_count}
      </span>
    ),
  },
  {
    id: 'total_amount',
    header: 'Total Amount (VND)',
    cell: ({ row }) => (
      <span
        style={{ color: 'var(--vn-fg-default)', fontVariantNumeric: 'tabular-nums' }}
      >
        {formatVND(row.original.total_amount)}
      </span>
    ),
  },
  {
    id: 'mail_sent_count',
    header: 'Emails Sent',
    cell: ({ row }) => (
      <span style={{ color: 'var(--vn-fg-default)' }}>{row.original.mail_sent_count}</span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => <CycleStatusPill status={row.original.status} />,
  },
];

/* ── Page ────────────────────────────────────────────────────────────────── */

/**
 * /reconciliation — paginated list of reconciliation cycles.
 * Layout: grid gap-4 → PageHeader → Toolbar (search + filters + display) → DataTable → Pagination.
 * Row click → /reconciliation/:cohortKey.
 *
 * Canonical list archetype: search Input in Toolbar `start` slot (maxWidth:320,
 * Search size={15}); Filters + Display-options in `end` slot.
 * Note: search is client-side over the loaded page (backend has no full-text param).
 */
export default function ReconciliationListPage() {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CycleStatus | ''>('');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [hidden, setHidden] = useState<Record<string, boolean>>({});

  const conditions: FilterCondition[] = [
    ...(periodFrom || periodTo
      ? [{ id: 'period', field: 'period', operator: 'between', value: `${periodFrom},${periodTo}` }]
      : []),
    ...(statusFilter ? [{ id: 'status', field: 'status', operator: 'is', value: statusFilter }] : []),
  ];
  const appliedFieldCount = new Set(conditions.map((c) => c.field)).size;

  const applyFilters = (next: FilterCondition[]) => {
    const period = next.find((c) => c.field === 'period');
    const status = next.find((c) => c.field === 'status');
    const [from = '', to = ''] = period ? period.value.split(',') : [];
    setPeriodFrom(from);
    setPeriodTo(to);
    setStatusFilter(status ? (status.value as CycleStatus) : '');
    setPage(1);
  };

  const visibleColumns = columns.filter((c) => hidden[c.id as string] !== true);

  const params: ReconciliationCyclesParams = {
    page,
    page_size: PAGE_SIZE,
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(periodFrom ? { period_from: periodFrom } : {}),
    ...(periodTo ? { period_to: periodTo } : {}),
  };

  const { data, isLoading, isError, isFetching, refetch } = useReconciliationCycles(params);

  const allRows: CycleListItem[] = data?.results ?? [];

  // Client-side search over cohort_key / period labels
  const needle = search.trim().toLowerCase();
  const rows = needle
    ? allRows.filter(
        (r) =>
          r.cohort_key.toLowerCase().includes(needle) ||
          r.period_startdate.includes(needle) ||
          r.period_enddate.includes(needle) ||
          r.status.toLowerCase().includes(needle),
      )
    : allRows;

  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? allRows.length;

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <PageHeader
          title="Reconciliation"
          description="Manage reconciliation cycles — review invoice status and send confirmation emails."
        />

        <Toolbar
          style={{ marginBottom: 0 }}
          start={
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search cycles…"
              startSlot={<Search size={15} />}
              style={{ maxWidth: 320 }}
            />
          }
          end={
            <>
              <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="secondary" startIcon={<Filter size={15} />}>
                    Filters{' '}
                    {appliedFieldCount ? <Badge tone="primary">{appliedFieldCount}</Badge> : null}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" style={{ padding: 0 }}>
                  <FilterBuilder
                    fields={FILTER_FIELDS}
                    defaultValue={conditions.length ? conditions : undefined}
                    onApply={(next) => {
                      applyFilters(next);
                      setFilterOpen(false);
                    }}
                    onClose={() => setFilterOpen(false)}
                  />
                </PopoverContent>
              </Popover>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                    Display options
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                  {columns.map((c) => {
                    const id = c.id as string;
                    return (
                      <DropdownMenuCheckboxItem
                        key={id}
                        checked={hidden[id] !== true}
                        onCheckedChange={(v) => setHidden((prev) => ({ ...prev, [id]: v !== true }))}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {typeof c.header === 'string' ? c.header : id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          }
        />

        {isError ? (
          <Alert
            tone="danger"
            title="Unable to load reconciliation cycles"
            actions={
              <Button size="sm" variant="ghost" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : isLoading ? (
          <TabLoading rows={8} />
        ) : (
          <DataTable
            columns={visibleColumns}
            data={rows}
            getRowId={(r) => r.cohort_key}
            onRowClick={(row) =>
              navigate(`/reconciliation/${encodeURIComponent(row.cohort_key)}`)
            }
            emptyState={
              <EmptyState
                icon={<CalendarRange size={24} />}
                title="No reconciliation cycles found"
                description="Try adjusting the filters."
              />
            }
            footer={(() => {
              const view = paginationView(page, PAGE_SIZE, allRows.length, total, pageCount);
              return (
                <Pagination
                  page={page}
                  pageCount={view.pageCount}
                  onPageChange={setPage}
                  summary={`${view.summary}${isFetching ? ' …' : ''}`}
                />
              );
            })()}
          />
        )}
      </div>
    </Container>
  );
}
